import React, { Component, Fragment, type ReactElement } from 'react'
import Footer from '../../common/Footer/Footer'
import HeaderWrapper from '../../common/Header/HeaderWrapper/HeaderWrapper'
import BackToTop from '../../common/BackToTop/BackToTop'
import HamburgerMenuHandler from '../../common/Header/HeaderHandler/HamburgerMenuHandler'
import SideBarMenuMobile from '../../common/SideBarMenuMobile/SideBarMenuMobile'
import LoadingState from '../../common/LoadingState/LoadingState'
import images from '../../common/images'
import '../../common/NavigationButton/style.scss'
import './style.scss'

const culturePrinciples = [
  { title: 'Entrepreurship', lines: ['「起業家マインド」を育む、', '競争に強い骨太なビジネスパーソンに。'] },
  { title: 'Enthusiasm', lines: ['「熱中」して成し遂げる。', '熱く働けば、未来が変わる。'] },
  { title: 'Diversity', lines: ['「多様性」こそがパワー', 'イノベーションを生み出し続けるチーム'] },
  { title: 'Openness', lines: ['最高に「オープネス」、', '常にフェアでオープンなコミュニケーション'] }
]

const cultureValues = [
  {
    title: '爆速',
    english: 'Be Bakusoku',
    paragraphs: [
      'スピードは価値だ。',
      '思案よりも実践、動きながら考えよう。',
      '誰よりも早く手を動かし、誰よりも速くカタチにしていく。',
      '爆速で未来を引き寄せよう。'
    ]
  },
  {
    title: '感謝',
    english: 'Always 360° Kansha',
    paragraphs: [
      '感謝はチームに活気をもたらすエネルギー。',
      'それひとつで、信頼は生まれ、チームは強くなる。',
      'メンバーに。ユーザーに。パートナーに。惜しまず伝えよう。',
      '「ありがとう」は最速で打てる最高のアクションだ。'
    ]
  },
  {
    title: '着実',
    english: 'Ippo Ippo',
    paragraphs: [
      '飛躍的な成長も、最初は小さな一歩から。',
      'いきなり全部はできなくていい。でも、やるべきことから逃げない。',
      '派手じゃなくてもいい。地味でもいい。でも、一歩を止めない。',
      '着実に進もう。止まらなければ、辿り着く。'
    ]
  },
  {
    title: '積極的',
    english: 'P.Y.H.U.',
    paragraphs: [
      '遠慮するな、創る側であれ。',
      '思い立ったらすぐ発言。完璧じゃなくていい。その積極性に価値がある。',
      'その一言、リアクション、学ぶ姿勢がチームを動かす。',
      '組織の歯車じゃ退屈だ。枠にハマるな。主役になろう。'
    ]
  }
]

function SectionTitle ({ title, subtitle, id }: { title: string, subtitle: string, id: string }): ReactElement {
  return (
    <h2 className='culture-sectionTitle' id={id}>
      <span>{title}</span>
      <span className='culture-sectionSubtitle'>{subtitle}</span>
    </h2>
  )
}

function CultureAction ({ href, label }: { href: string, label: string }): ReactElement {
  const external = href.startsWith('https://')
  return (
    <a className='nav-button culture-action' href={href} target={external ? '_blank' : undefined} rel={external ? 'noreferrer' : undefined}>
      <span className='nav-buttonText'>
        {label}
        <img src={images.cultureButtonArrow} alt='' />
      </span>
    </a>
  )
}

class Culture extends Component {
  componentDidMount(): void {
    document.title = 'IVA｜AI × ヒトで、世界にもっと輝きを。｜カルチャー'
  }

  render(): ReactElement {
    return (
      <Fragment>
        <div className='culture main-content'>
          <LoadingState />
          <HeaderWrapper shouldShowBlackLogo={true} />
          <BackToTop />
          <main className='culture-main' aria-label='カルチャー'>
            <img className='culture-background culture-backgroundTop' src={images.cultureDesignBackground} alt='' />
            <section className='culture-section culture-mission container' aria-labelledby='culture-mission-title'>
              <div className='culture-split'>
                <SectionTitle id='culture-mission-title' title='Mission' subtitle='私たちの使命' />
                <div className='culture-missionContent'>
                  <div className='culture-missionCopy'>
                    <h1 className='culture-heading'>モノの可能性を最大化し、<br />価値経済の統合基盤を築く。</h1>
                    <p className='culture-missionEnglish'>Maximize the Potential of Assets.<br />Build the Foundation of the Value Economy.</p>
                  </div>
                  <div className='culture-actions'>
                    <CultureAction href='/message' label='CEOメッセージ' />
                    <CultureAction href='https://herp.careers/v1/ivacorp' label='職種環境を見る' />
                  </div>
                </div>
              </div>
            </section>
            <section className='culture-section culture-overview container' aria-labelledby='culture-overview-title'>
              <SectionTitle id='culture-overview-title' title='Culture' subtitle='カルチャー' />
              <div className='culture-overviewContent'>
                <div className='culture-overviewText'>
                  <h3 className='culture-heading'>価値やポテンシャルを<br />最大限に開放していくことがIVAの使命</h3>
                  <div className='culture-body'>
                    <p>一人ひとりが卓越した能力を持ち、日々の学びを怠らない姿勢は、個人のみならずチームに良い影響を及ぼします。 私たちは「期待の一歩先」「常識の一歩先」を追求し続けます。</p>
                    <p>スピードは価値です。業務を能率化し、あらゆる局面において常に最速を目指します。これまでにない革新的な存在であり続けることを目指します。前例にとらわれず挑戦し、大胆に、リスクを恐れず創造した価値は、やがて歴史となります。</p>
                  </div>
                </div>
                <img className='culture-art' src={images.cultureDesignArt} alt='Entrepreurship、Enthusiasm、Diversity、Opennessを表す地球のイラスト' />
              </div>
              <ul className='culture-principles'>
                {culturePrinciples.map(principle => (
                  <li key={principle.title}>
                    <h3>{principle.title}</h3>
                    <p>{principle.lines.map((line, index) => <Fragment key={line}>{index > 0 && <br />}{line}</Fragment>)}</p>
                  </li>
                ))}
              </ul>
            </section>
            <section className='culture-section culture-vision container' aria-labelledby='culture-vision-title'>
              <SectionTitle id='culture-vision-title' title='Vision' subtitle='私たちの野望' />
              <div className='culture-visionContent'>
                <div className='culture-visionHeadline'>
                  <h3 className='culture-heading'>価値の覚醒で、<br />世界に熱狂を。</h3>
                  <p>Unlock Value. Power the World.</p>
                </div>
                <div className='culture-body'>
                  <p>世界には未だ、ポテンシャルを秘めた価値が無数にある。</p>
                  <p>
                    真価を見出されず、過小に評価されている価値。<br />
                    忘れ去られ、巡ることなく眠り続けている価値。<br />
                    信頼という壁に阻まれ、流動性を失っている価値。
                  </p>
                  <p>
                    私たちは、あらゆる価値を解き放つ。<br />
                    価値が正しく見出され、適切に評価され、自在に巡る世界へ。
                  </p>
                  <p>
                    モノも、人も、企業も、社会も。<br />
                    あらゆる価値が、本来の可能性を発揮できる世界をつくる。
                  </p>
                </div>
              </div>
            </section>
            <section className='culture-section culture-values container' aria-labelledby='culture-values-title'>
              <SectionTitle id='culture-values-title' title='Operating System (OS)' subtitle='私たちの行動指針' />
              <div className='culture-valueGrid'>
                {cultureValues.map((value, index) => (
                  <article className='culture-valueCard' key={value.english}>
                    <div className='culture-valueTop'>
                      <h3>{value.title}</h3>
                      <span className='culture-valueNumber'>{String(index + 1).padStart(2, '0')}</span>
                    </div>
                    <p className='culture-valueEnglish' lang='en'>{value.english}</p>
                    <div className='culture-valueLine' aria-hidden='true' />
                    <div className='culture-body'>{value.paragraphs.map(paragraph => <p key={paragraph}>{paragraph}</p>)}</div>
                  </article>
                ))}
              </div>
              <div className='culture-actions culture-valuesActions'>
                <CultureAction href='https://herp.careers/v1/ivacorp' label='職種環境を見る' />
                <CultureAction href='https://www.wantedly.com/companies/company_6154268' label='Wantedly 採用ページ' />
              </div>
            </section>
            <img className='culture-background culture-backgroundBottom' src={images.cultureDesignBackground} alt='' />
          </main>
          <Footer />
        </div>
        <SideBarMenuMobile handler={new HamburgerMenuHandler(true)} />
      </Fragment>
    )
  }
}

export default Culture
